/**
 * Preview — the dashboard exactly as an end user will see it.
 *
 * Renders `DashboardGrid`, the same component the published viewer uses, so
 * this is a true preview rather than a separate mock rendering path. The only
 * addition is the banner reminding the admin they are still in the builder.
 */

import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import HeaderLogo from '../components/header/HeaderLogo';
import DashboardGrid from './components/DashboardGrid';
import DashboardSelectorBar from './components/DashboardSelectorBar';
import { GRID_GAP } from './utils/dashboardUtils';
import { selectorFiltersFor, type SelectorState, type SelectorValue } from './utils/selectorRuntime';
import type { DashboardConfig } from './types/dashboardTypes';

interface Props {
  title: string;
  config: DashboardConfig;
  onClose: () => void;
}

export default function DashboardPreview({ title, config, onClose }: Props) {
  const logoRight = config.theme.logoPosition === 'right';
  const { headerBgColor, headerTextColor } = config.theme;
  const logo = <HeaderLogo url={config.theme.logoUrl} size={config.theme.logoSize} />;

  // See DashboardViewer's identical block — the preview has to behave the
  // same way the published dashboard will, selectors included.
  const [selectorValues, setSelectorValues] = useState<SelectorState>({});
  const selectorFilters = useMemo(
    () =>
      selectorFiltersFor(
        config.dataSources.map((source) => source.id),
        config.selectors,
        selectorValues,
      ),
    [config.dataSources, config.selectors, selectorValues],
  );
  const setSelectorValue = (id: string, value: SelectorValue | undefined) =>
    setSelectorValues((current) => {
      if (!value) {
        if (!(id in current)) return current;
        const next = { ...current };
        delete next[id];
        return next;
      }
      return { ...current, [id]: value };
    });

  return (
    <div className="fixed inset-0 z-[9500] flex flex-col bg-slate-100">
      <div
        className="flex flex-shrink-0 items-center gap-3 border-b border-slate-200 bg-white px-5 py-3"
        // Same header styling the published viewer applies, so the "not yet
        // published" banner still shows the logo position / colours truthfully
        // — the one thing this bar does NOT preview is the eyebrow/title copy
        // below, which is builder chrome and never ships.
        style={headerBgColor ? { background: headerBgColor, borderColor: headerBgColor } : undefined}
      >
        {/* The banner is builder chrome, but the logo is not: it is part of
            what ships, and the preview is the only place to check it before
            publishing. */}
        {!logoRight && logo}

        <div className="min-w-0 max-w-xs flex-shrink">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-600">
            Preview — not yet published
          </p>
          <h2
            className="truncate text-base font-semibold text-slate-900"
            style={headerTextColor ? { color: headerTextColor } : undefined}
          >
            {title}
          </h2>
        </div>

        {/* Fills whatever's left of THIS one row — see DashboardSelectorBar's
            own note and DashboardViewer's identical layout. */}
        <DashboardSelectorBar
          selectors={config.selectors}
          dataSources={config.dataSources}
          values={selectorValues}
          onChange={setSelectorValue}
        />

        <button
          type="button"
          onClick={onClose}
          className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
        >
          <X className="h-4 w-4" />
          Close preview
        </button>

        {logoRight && logo}
      </div>

      {/* One page: the grid gets whatever height is left and divides it between
          its rows, so nothing here may scroll. Padding equals the tile gap, so
          the outer margin matches the inner gutters. */}
      {/* A dashboard set to "Always dark" scopes the dark tokens to itself, so
          it stays dark inside a light application. "Follow application" sets no
          attribute and simply inherits. */}
      <div
        data-theme={config.theme.mode === 'dark' ? 'dark' : undefined}
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
        style={{ padding: GRID_GAP, background: 'var(--bv-surface)' }}
      >
        {config.description && (
          <p
            className="max-w-3xl flex-shrink-0 text-sm text-slate-600"
            style={{ marginBottom: GRID_GAP }}
          >
            {config.description}
          </p>
        )}
        <div className="min-h-0 flex-1">
        <DashboardGrid
          widgets={config.widgets}
          dataSources={config.dataSources}
          refreshInterval={config.refreshInterval}
          accentColor={config.theme.accentColor}
          themeMode={config.theme.mode}
          dividerColor={config.theme.dividerColor}
          selectorFilters={selectorFilters}
        />
        </div>
      </div>
    </div>
  );
}
